import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Users, Building2, TrendingUp, ArrowLeft, Loader2, Mail, Edit, CheckCircle2 } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { trackTikTokEvent, identifyTikTokUser } from '@/lib/tiktokTracking';
import { trackXAdsEvent, identifyXUser } from '@/lib/xAdsTracking';
import { supabase } from '@/integrations/supabase/client';
import { OtpInput } from '@/components/auth/OtpInput';

// Helper function to get email provider info
const getEmailProvider = (email: string): { name: string; url: string } | null => {
  const domain = email.split('@')[1]?.toLowerCase();
  
  const providers: Record<string, { name: string; url: string }> = {
    'gmail.com': { name: 'Gmail', url: 'https://mail.google.com' },
    'googlemail.com': { name: 'Gmail', url: 'https://mail.google.com' },
    'outlook.com': { name: 'Outlook', url: 'https://outlook.live.com/mail' },
    'hotmail.com': { name: 'Outlook', url: 'https://outlook.live.com/mail' },
    'live.com': { name: 'Outlook', url: 'https://outlook.live.com/mail' },
    'yahoo.com': { name: 'Yahoo', url: 'https://mail.yahoo.com' },
    'icloud.com': { name: 'iCloud', url: 'https://www.icloud.com/mail' },
    'protonmail.com': { name: 'ProtonMail', url: 'https://mail.proton.me' },
  };
  
  return providers[domain] || null;
};

/** Shared premium page wrapper with decorative blur elements */
const AuthPageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div data-auth-card className="min-h-screen bg-background flex flex-col items-center justify-center p-4 relative overflow-hidden">
    {/* Decorative blur elements */}
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-brand/5 blur-3xl rounded-full" />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-purple-500/[0.03] blur-3xl rounded-full" />
    </div>
    <div className="relative z-10 w-full max-w-md">
      {children}
    </div>
  </div>
);

/** Shared premium logo header */
const AuthLogo = () => (
  <div className="flex items-center justify-center gap-2.5 mb-6">
    <div className="icon-brand">
      <Shield className="w-8 h-8" />
    </div>
    <span className="text-xl font-semibold tracking-tight text-foreground">
      CreatorVault
    </span>
  </div>
);

/** Premium card classes */
const premiumCardClass = "border border-border/30 bg-card shadow-premium rounded-2xl transition-premium";

/** Premium input classes */
const premiumInputClass = "h-11 bg-background border-border/60 focus:border-brand/50 rounded-xl transition-premium";

/** Premium primary button classes */
const premiumButtonClass = "w-full h-12 font-medium rounded-xl transition-premium";

const Auth = () => {
  const { user, profile, signUp, signIn, loading, resendVerificationEmail, resetPassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Default to Sign IN. Returning users who land on /auth without ?mode=
  // were being shown Sign UP first, then hitting "User already registered"
  // when they typed their existing credentials — easy to misread as a
  // password failure. Marketing CTAs that want signup pass ?mode=signup.
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const hasTrackedViewContent = useRef(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [otpExpiry, setOtpExpiry] = useState(600); // 10 minutes

  // Track ViewContent on page load (only once)
  useEffect(() => {
    if (!hasTrackedViewContent.current) {
      hasTrackedViewContent.current = true;
      trackTikTokEvent('ViewContent', {
        properties: { 
          content_name: 'Auth Page', 
          content_type: 'signup_page',
          signup_mode: isSignUp ? 'signup' : 'signin'
        }
      });
    }
  }, []);

  // URL mode detection - only runs when searchParams changes, NOT on loading changes
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'signin') {
      console.log('🔵 Setting isSignUp to FALSE (signin mode)');
      setIsSignUp(false);
    } else if (mode === 'signup') {
      console.log('🟢 Setting isSignUp to TRUE (signup mode)');
      setIsSignUp(true);
    }

    // Pre-fill email if provided in URL
    const emailParam = searchParams.get('email');
    if (emailParam) {
      const decodedEmail = decodeURIComponent(emailParam);
      console.log('📧 Pre-filling email from URL:', decodedEmail);
      setEmail(decodedEmail);
    }
  }, [searchParams]);

  // Redirect after auth - separate from mode detection.
  // Navigate DIRECTLY to the role's home (replace:true) instead of bouncing
  // through /dashboard. The intermediate /dashboard hop caused a triple-mount
  // chain that, combined with stale lazy-chunk references after deploys,
  // produced a blank-white screen until a hard refresh. See lazyWithRetry +
  // RouteErrorBoundary for the lazy-chunk safety net.
  useEffect(() => {
    console.log('Auth redirect check:', { user: !!user, profile: !!profile, loading });
    if (user && profile && !loading) {
      const returnUrl = sessionStorage.getItem('returnAfterAuth');
      
      if (returnUrl) {
        console.log('Redirecting to stored return URL:', returnUrl);
        sessionStorage.removeItem('returnAfterAuth');
        navigate(returnUrl, { replace: true });
        return;
      }
      if (!profile.onboarding_completed) {
        console.log('Navigating to onboarding');
        navigate('/onboarding', { replace: true });
        return;
      }
      let target = '/dashboard';
      if (profile.role === 'business') target = '/business/dashboard';
      else if (profile.role === 'creator' || profile.role === 'demo') target = '/creator/dashboard';
      console.log('Navigating directly to role home:', target);
      navigate(target, { replace: true });
    }
  }, [user, profile, loading, navigate]);

  // OTP expiry countdown
  useEffect(() => {
    if (!showVerificationPrompt || otpExpiry <= 0) return;
    const timer = setTimeout(() => setOtpExpiry(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [showVerificationPrompt, otpExpiry]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Guard: if user exists but profile is still loading, show spinner instead of form.
  // Includes an escape hatch — after 8s, surface a retry hint so users are never
  // trapped on an indefinite spinner if the profile fetch silently fails (e.g.
  // RLS regression). See PR #42 for the original incident this guards against.
  const [profileLoadTimedOut, setProfileLoadTimedOut] = useState(false);
  useEffect(() => {
    if (!user || profile) {
      setProfileLoadTimedOut(false);
      return;
    }
    const t = setTimeout(() => setProfileLoadTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [user, profile]);

  if (user && !profile) {
    return (
      <AuthPageWrapper>
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
          <p className="text-muted-foreground text-sm">Loading your account...</p>
          {profileLoadTimedOut && (
            <div className="flex flex-col items-center gap-2 text-center max-w-sm">
              <p className="text-sm text-muted-foreground">
                This is taking longer than expected. Try reloading the page or signing out.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="text-sm underline text-brand"
                >
                  Reload
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await supabase.auth.signOut();
                    window.location.href = '/auth';
                  }}
                  className="text-sm underline text-muted-foreground"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </AuthPageWrapper>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🚀 Form submission:', {
      isSignUp,
      mode: searchParams.get('mode'),
      callingFunction: isSignUp ? 'signUp()' : 'signIn()',
      email
    });
    
    if (isSignUp && password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please re-enter the same password in both fields.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (isSignUp) {
        console.log('✅ Calling signUp() function - name will be collected during onboarding');
        const { error } = await signUp(email, password, {});
        
        if (!error) {
          // TikTok SubmitForm — signup form was submitted successfully. This is
          // the mid-funnel event between ViewContent (landed on /auth) and
          // CompleteRegistration (finished onboarding). Non-blocking.
          try {
            await trackTikTokEvent('SubmitForm', {
              user: { email },
              properties: {
                content_type: 'signup_submit',
                content_name: 'Signup form submitted',
                signup_mode: 'signup',
                source: 'auth_page',
              },
            });
          } catch (trackErr) {
            console.warn('[TikTok] SubmitForm tracking failed (non-blocking):', trackErr);
          }
          // Mirror to X Ads: Signup event fires both browser pixel (if
          // VITE_X_EVENT_TAG_SIGNUP configured) and server-side CAPI.
          try {
            await trackXAdsEvent('Signup', {
              user: { email },
              properties: {
                content_type: 'signup_submit',
                content_name: 'Signup form submitted',
              },
            });
          } catch (xErr) {
            console.warn('[X] Signup tracking failed (non-blocking):', xErr);
          }

          const pendingVerificationEmail = localStorage.getItem('pendingVerificationEmail');
          if (pendingVerificationEmail) {
            setPendingEmail(pendingVerificationEmail);
            setShowVerificationPrompt(true);
            localStorage.removeItem('pendingVerificationEmail');
          }
        }
      } else {
        console.log('✅ Calling signIn() function');
        const { error } = await signIn(email, password);
        
        if (!error) {
          // TikTok pixel identify — now that the user is logged in, tag all
          // future pixel events with their identity. Higher EMQ = better
          // attribution.
          try {
            identifyTikTokUser({ email });
            identifyXUser({ email });
          } catch (idErr) {
            console.warn('[TikTok/X] identify failed (non-blocking):', idErr);
          }
          // Redirect will happen automatically via useAuth hook
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) return;
    setVerifying(true);
    try {
      // Use 'email' type — works for both signup-confirmation and
      // magiclink/recovery OTPs (the edge function uses generateLink({ type: 'magiclink' }),
      // which produces a recovery-type OTP for already-confirmed users).
      const { data, error } = await supabase.auth.verifyOtp({
        email: pendingEmail,
        token: otpCode,
        type: 'email',
      });

      if (error) {
        toast({
          title: "Verification Failed",
          description: error.message === 'Token has expired or is invalid'
            ? "This code has expired. Click 'Resend code' to get a new one."
            : error.message,
          variant: "destructive",
        });
        return;
      }

      if (data.session) {
        setVerified(true);
        toast({
          title: "Email Verified!",
          description: "Your account is ready. Welcome to CreatorVault!",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    const result = await resendVerificationEmail(pendingEmail);
    if (!result.error || result.error.message?.includes('rate limit') || result.error.message?.includes('429')) {
      setResendCooldown(60);
      setOtpExpiry(600);
      setOtpCode('');
    }
  };

  // Verification prompt view — OTP code entry
  if (showVerificationPrompt) {
    return (
      <AuthPageWrapper>
        <Card className={premiumCardClass}>
          <CardHeader className="text-center pb-4 pt-8 px-8">
            <AuthLogo />
            {verified ? (
              <>
                <div className="flex justify-center mb-2">
                  <CheckCircle2 className="w-16 h-16 text-green-500 animate-in zoom-in-50 duration-300" />
                </div>
                <CardTitle className="text-2xl font-semibold tracking-tight">Verified!</CardTitle>
                <CardDescription className="text-muted-foreground mt-2">
                  Redirecting you to your dashboard...
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-2xl font-semibold tracking-tight">Enter Verification Code</CardTitle>
                <CardDescription className="text-muted-foreground mt-2">
                  We sent a 6-digit code to <strong>{pendingEmail}</strong>
                </CardDescription>
              </>
            )}
          </CardHeader>

          {!verified && (
            <CardContent className="px-8 pb-8 space-y-5">
              <OtpInput value={otpCode} onChange={setOtpCode} onComplete={handleVerifyOtp} />

              {otpExpiry > 0 ? (
                <p className="text-center text-xs text-muted-foreground">
                  Code expires in {Math.floor(otpExpiry / 60)}:{(otpExpiry % 60).toString().padStart(2, '0')}
                </p>
              ) : (
                <p className="text-center text-xs text-destructive">
                  Code expired — request a new one below
                </p>
              )}

              <Button
                onClick={handleVerifyOtp}
                variant="brand"
                className={premiumButtonClass}
                disabled={otpCode.length !== 6 || verifying || otpExpiry <= 0}
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>

              <Button
                onClick={handleResendCode}
                variant="outline"
                className="w-full h-11 rounded-xl border-border/60 transition-premium"
                disabled={resendCooldown > 0}
              >
                <Mail className="w-4 h-4 mr-2" />
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
              </Button>

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowVerificationPrompt(false);
                    setOtpCode('');
                    setIsSignUp(true);
                  }}
                  variant="outline"
                  className="flex-1 h-11 rounded-xl border-border/60 transition-premium text-xs"
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Wrong email?
                </Button>

                <Button
                  onClick={() => {
                    setShowVerificationPrompt(false);
                    setOtpCode('');
                    setIsSignUp(false);
                  }}
                  variant="ghost"
                  className="flex-1 h-11 transition-premium text-xs"
                >
                  Back to Login
                </Button>
              </div>

              <p className="text-center text-xs text-muted-foreground">
                Check your spam folder if you don't see the email
              </p>
            </CardContent>
          )}
        </Card>
      </AuthPageWrapper>
    );
  }

  // Forgot Password Sent Confirmation
  if (forgotPasswordSent) {
    const provider = getEmailProvider(forgotPasswordEmail);
    return (
      <AuthPageWrapper>
        <Card className={premiumCardClass}>
          <CardHeader className="text-center pb-6 pt-8 px-8">
            <AuthLogo />
            <CardTitle className="text-2xl font-semibold tracking-tight">Check Your Email</CardTitle>
            <CardDescription className="text-muted-foreground mt-2">
              We've sent a password reset link to your email.
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8 space-y-5">
            <Alert className="bg-card border-border/30">
              <Mail className="h-4 w-4" />
              <AlertDescription className="text-sm">
                If an account exists for <strong>{forgotPasswordEmail}</strong>, you'll receive a password reset link shortly.
                <br /><br />
                <span className="text-xs text-muted-foreground">
                  Don't see the email? Check your spam folder. The link expires in 1 hour.
                </span>
              </AlertDescription>
            </Alert>

            {provider && (
              <Button
                onClick={() => window.open(provider.url, '_blank', 'noopener,noreferrer')}
                variant="brand"
                className={premiumButtonClass}
              >
                <Mail className="w-4 h-4 mr-2" />
                Open {provider.name}
              </Button>
            )}

            <Button
              onClick={() => {
                setForgotPasswordSent(false);
                setShowForgotPassword(false);
                setForgotPasswordEmail('');
                setIsSignUp(false);
              }}
              variant="outline"
              className="w-full h-11 rounded-xl border-border/60 transition-premium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </AuthPageWrapper>
    );
  }

  // Forgot Password Form
  if (showForgotPassword) {
    return (
      <AuthPageWrapper>
        <Card className={premiumCardClass}>
          <CardHeader className="text-center pb-6 pt-8 px-8">
            <AuthLogo />
            <CardTitle className="text-2xl font-semibold tracking-tight">Reset Password</CardTitle>
            <CardDescription className="text-muted-foreground mt-2">
              Enter your email to receive a password reset link
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8 space-y-5">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setIsSubmitting(true);
                const { error } = await resetPassword(forgotPasswordEmail);
                setIsSubmitting(false);
                if (!error) {
                  setForgotPasswordSent(true);
                }
              }}
              className="space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-sm font-medium">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className={premiumInputClass}
                  required
                />
              </div>

              <Button
                type="submit"
                variant="brand"
                className={premiumButtonClass}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Reset Link
                  </>
                )}
              </Button>
            </form>

            <Button
              onClick={() => {
                setShowForgotPassword(false);
                setForgotPasswordEmail('');
              }}
              variant="ghost"
              className="w-full h-11 transition-premium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </AuthPageWrapper>
    );
  }

  return (
    <AuthPageWrapper>
      {/* Back to home link */}
      <Link 
        to="/" 
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-premium mb-8"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to home
      </Link>

      {/* Main auth card */}
      <Card className={premiumCardClass}>
        <CardHeader className="text-center pb-6 pt-8 px-8">
          {/* Logo */}
          <AuthLogo />
          
          {/* Title & subtitle */}
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </CardTitle>
          <CardDescription className="text-muted-foreground mt-2">
            {isSignUp ? 'Get started with CreatorVault' : 'Sign in to your account'}
          </CardDescription>
        </CardHeader>

        <CardContent className="px-8 pb-8 space-y-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={premiumInputClass}
                required
              />
            </div>

            {/* Password field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setForgotPasswordEmail(email);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-premium"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={premiumInputClass}
                required
              />
            </div>

            {/* Confirm password (signup only) */}
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirm Password
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={premiumInputClass}
                  required
                />
              </div>
            )}

            {/* Submit button */}
            <Button 
              type="submit" 
              variant="brand"
              className={premiumButtonClass}
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {isSignUp ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                <>
                  {isSignUp ? 'Create Account' : 'Sign In'}
                  <TrendingUp className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Toggle sign in/up */}
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-sm text-muted-foreground hover:text-foreground transition-premium"
            >
              {isSignUp 
                ? 'Already have an account? Sign in' 
                : "Don't have an account? Sign up"
              }
            </button>
          </div>

          {/* Terms (signup only) */}
          {isSignUp && (
            <p className="text-xs text-muted-foreground text-center leading-relaxed">
              By creating an account, you agree to our{' '}
              <Link to="/terms-of-service" className="text-primary hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </AuthPageWrapper>
  );
};

export default Auth;
